import { Component, OnDestroy, OnInit } from '@angular/core'
import { ModalController } from '@ionic/angular/standalone'
import { CacheService } from '../services/business/cache.service'
import { Account } from 'src/app/account/models/account.model'
import { Utils } from '../utils/utils'
import { ApiService } from '../services/api/api.service'
import { GoogleAnalyticsEvents } from '../enums/google-analytics-events.enum'
import { ProvisionReadinessStatus } from '../models/provision-readiness'

@Component({
    selector: 'app-add-card-to-wallet',
    templateUrl: './add-card-to-wallet.component.html',
    styleUrls: ['./add-card-to-wallet.component.scss'],
})
export class AddCardToWalletComponent implements OnInit, OnDestroy {

    account: Account
    walletType = ''
    tokenlink: string

    // View state driven by the Provision Readiness API.
    isCheckingReadiness = true
    isPreparingCard = false
    cannotProvision = false

    private static readonly ReadinessPollIntervalMs = 10000
    private pollHandle?: ReturnType<typeof setInterval>

    constructor(
        private modalController: ModalController,
        private cacheService: CacheService,
        public apiService: ApiService
    ) {
    }

    ngOnInit(): void {
        this.account = this.cacheService.getSelectedAccount()
        this.walletType = Utils.detectMobileOS() === 'iOS' ? 'Apple' : 'Google'
        void this.checkReadiness()
    }

    ngOnDestroy(): void {
        this.stopPolling()
    }

    public async dismissModal(): Promise<void> {
        this.stopPolling()
        await this.modalController.dismiss()
    }

    // Calls the Provision Readiness API and reacts to the returned status.
    private async checkReadiness(): Promise<void> {
        try {
            const readiness = await this.apiService.getProvisionReadiness()

            switch (readiness?.status as ProvisionReadinessStatus) {
                case ProvisionReadinessStatus.ReadyForProvisioning:
                    await this.provisionCard()
                    break
                case ProvisionReadinessStatus.Pending:
                    this.enterPreparingState()
                    break
                case ProvisionReadinessStatus.CannotProvision:
                default:
                    this.enterCannotProvisionState()
                    break
            }
        } catch {
            this.enterCannotProvisionState()
        }
    }

    // Ready to provision: fetch the tokenized link and enable the LINK2WALLET button.
    private async provisionCard(): Promise<void> {
        const link2Wallet = await this.apiService.getLink2Wallet()

        if (link2Wallet?.tokenizedLink) {
            this.tokenlink = link2Wallet.tokenizedLink
            this.isCheckingReadiness = false
            this.isPreparingCard = false
            this.cannotProvision = false
            this.stopPolling()
        } else {
            // The card is not quite ready yet; keep showing the preparing state and polling.
            this.enterPreparingState()
        }
    }

    private enterPreparingState(): void {
        this.isCheckingReadiness = false
        this.isPreparingCard = true
        this.cannotProvision = false
        this.startPolling()
    }

    private enterCannotProvisionState(): void {
        this.stopPolling()
        this.isCheckingReadiness = false
        this.isPreparingCard = false
        this.cannotProvision = true
    }

    // Re-check readiness every 10 seconds while the card is being prepared.
    private startPolling(): void {
        if (this.pollHandle) {
            return
        }
        this.pollHandle = setInterval(() => {
            void this.checkReadiness()
        }, AddCardToWalletComponent.ReadinessPollIntervalMs)
    }

    private stopPolling(): void {
        if (this.pollHandle) {
            clearInterval(this.pollHandle)
            this.pollHandle = undefined
        }
    }

    public get googleAnalyticsEvents(): typeof GoogleAnalyticsEvents {
        return GoogleAnalyticsEvents
    }
}
